/**
 * The booking sheet: one shared BottomSheetSwitcher, steps driven by the URL.
 * service → time → contacts → confirm → done. The client never sends price,
 * duration or tenant; the idempotency key survives retries and reloads.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Banner } from '@astryxdesign/core/Banner';
import { BottomSheet, BottomSheetSwitcher } from '@astryxdesign/core/BottomSheet';
import { Button } from '@astryxdesign/core/Button';
import { Heading } from '@astryxdesign/core/Heading';
import { Icon } from '@astryxdesign/core/Icon';
import { HStack, VStack } from '@astryxdesign/core/Layout';
import { List, ListItem } from '@astryxdesign/core/List';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { Text } from '@astryxdesign/core/Text';
import { TextArea } from '@astryxdesign/core/TextArea';
import { TextInput } from '@astryxdesign/core/TextInput';
import { ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { ApiError } from '@shared/errors.ts';
import type { Slot } from '@shared/contracts.ts';
import { useTenant } from '@/app/tenant-context.ts';
import { errorText, publicApi } from '@/lib/api.ts';
import { duration, longDateTime, money, normalizePhone, dateTime } from '@/lib/format.ts';
import { clearPending, pendingKey, saveBooking } from '@/lib/storage.ts';
import { bookingDays } from './days.ts';
import { useBookingNav } from './nav.ts';
import { SlotPicker } from './SlotPicker.tsx';
import { ReminderControls } from '@/features/my/ReminderControls.tsx';
import { useIndustry } from '@/lib/industry.ts';

interface Contacts {
  name: string;
  phone: string;
  car: string;
  comment: string;
}
const CONTACT_KEY = 'contact:v1';
function loadContacts(): Contacts {
  try {
    const saved = JSON.parse(localStorage.getItem(CONTACT_KEY) ?? '{}') as Partial<Contacts>;
    return { name: saved.name ?? '', phone: saved.phone ?? '', car: saved.car ?? '', comment: '' };
  } catch {
    return { name: '', phone: '', car: '', comment: '' };
  }
}
function rememberContacts(c: Contacts) {
  try {
    localStorage.setItem(CONTACT_KEY, JSON.stringify({ name: c.name, phone: c.phone, car: c.car }));
  } catch {
    /* ignore */
  }
}

export function BookingFlow() {
  const { slug, tenant, base } = useTenant();
  const copy = useIndustry();
  const nav = useBookingNav();
  const qc = useQueryClient();
  const [contacts, setContacts] = useState<Contacts>(loadContacts);
  const [errors, setErrors] = useState<Partial<Record<keyof Contacts, string>>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; token: string } | null>(null);

  const service = tenant.services.find((s) => s.id === nav.serviceId) ?? null;
  const days = useMemo(() => bookingDays(tenant), [tenant]);

  // Guard against stale/invalid URLs: fall back to the right step.
  const step = (() => {
    if (!nav.step) return null;
    if (nav.step === 'done') return created ? 'done' : null;
    if (!service) return 'service';
    if ((nav.step === 'contacts' || nav.step === 'confirm') && !nav.startsAt) return 'time';
    return nav.step;
  })();

  useEffect(() => {
    if (!nav.step) {
      setSubmitError(null);
      setNotice(null);
      clearPending(slug);
    }
  }, [nav.step, slug]);

  const slotEnd = service && nav.startsAt ? new Date(new Date(nav.startsAt).getTime() + service.duration_minutes * 60_000).toISOString() : null;

  const validateContacts = () => {
    const e: typeof errors = {};
    if (!contacts.name.trim()) e.name = 'Как к вам обращаться?';
    if (!normalizePhone(contacts.phone)) e.phone = 'Телефон в формате +7 999 123-45-67';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!service || !nav.startsAt) return;
    const phone = normalizePhone(contacts.phone)!;
    const body = {
      serviceId: service.id,
      startsAt: nav.startsAt,
      name: contacts.name.trim(),
      phone,
      car: copy.hasCar ? contacts.car.trim() : '',
      comment: contacts.comment.trim(),
      source: 'client' as const,
    };
    // Same data → same key → the server returns the same booking (and token) on retry.
    const idempotencyKey = pendingKey(slug, JSON.stringify(body));
    setSubmitError(null);
    try {
      const res = await publicApi.createBooking(slug, { ...body, idempotencyKey });
      saveBooking(slug, { id: res.booking.booking_id, token: res.accessToken, startsAt: nav.startsAt, serviceName: service.name });
      rememberContacts(contacts);
      setCreated({ id: res.booking.booking_id, token: res.accessToken });
      void qc.invalidateQueries({ queryKey: ['slots', slug] });
      void qc.invalidateQueries({ queryKey: ['my-bookings', slug] });
      nav.go({ step: 'done', bookingId: res.booking.booking_id }, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && (e.code === 'slot_taken' || e.code === 'invalid_slot')) {
        clearPending(slug);
        setNotice(errorText(e));
        void qc.invalidateQueries({ queryKey: ['slots', slug] });
        nav.go({ step: 'time', startsAt: null }, { replace: true });
        return;
      }
      setSubmitError(errorText(e));
    }
  };

  const back = () => window.history.back();

  return (
    <BottomSheetSwitcher activeSheet={step} onActiveSheetChange={(s) => (s === null ? nav.close() : undefined)}>
      <BottomSheet sheetId="service" label="Выбор услуги" height="capped">
        <VStack gap={3}>
          <Heading level={2}>Запись в {copy.placeAcc}</Heading>
          <Text type="supporting">Выберите услугу</Text>
          <List hasDividers>
            {tenant.services.map((s) => (
              <ListItem
                key={s.id}
                label={s.name}
                description={duration(s.duration_minutes)}
                endContent={<Text weight="semibold">{money(s.price_cents, tenant.currency, s.price_is_from)}</Text>}
                isSelected={s.id === nav.serviceId}
                onClick={() => nav.go({ step: 'time', serviceId: s.id, startsAt: null })}
              />
            ))}
          </List>
        </VStack>
      </BottomSheet>

      <BottomSheet sheetId="time" label="Выбор времени" height="tall">
        <VStack gap={4}>
          <SheetHeader title={service?.name ?? 'Время'} subtitle={service ? `${duration(service.duration_minutes)} · ${money(service.price_cents, tenant.currency, service.price_is_from)}` : ''} onBack={back} />
          {notice && <Banner status="warning" title={notice} collapsible={false} isDismissable onDismiss={() => setNotice(null)} />}
          {service && (
            <SlotPicker
              days={days}
              day={nav.day}
              onDayChange={(d) => nav.go({ day: d, startsAt: null }, { replace: true })}
              selected={nav.startsAt}
              onSelect={(s: Slot) => nav.go({ step: 'contacts', startsAt: s.starts_at, day: s.day })}
              timezone={tenant.timezone}
              queryKey={['slots', slug, service.id]}
              fetchDay={(d, n) => publicApi.slots(slug, service.id, d, n)}
            />
          )}
        </VStack>
      </BottomSheet>

      <BottomSheet sheetId="contacts" label="Контакты" height="tall" purpose="form">
        <VStack gap={4}>
          <SheetHeader title="Ваши контакты" subtitle={nav.startsAt ? dateTime(nav.startsAt, tenant.timezone) : ''} onBack={back} />
          <TextInput label="Имя" value={contacts.name} onChange={(v) => setContacts({ ...contacts, name: v })} autoComplete="name" isRequired status={errors.name ? { type: 'error', message: errors.name } : undefined} width="100%" />
          <TextInput
            label="Телефон"
            value={contacts.phone}
            onChange={(v) => setContacts({ ...contacts, phone: v })}
            autoComplete="tel"
            placeholder="+7 999 123-45-67"
            isRequired
            status={errors.phone ? { type: 'error', message: errors.phone } : undefined}
            width="100%"
          />
          {copy.hasCar && (
            <TextInput label="Автомобиль" value={contacts.car} onChange={(v) => setContacts({ ...contacts, car: v })} placeholder="Марка, модель" isOptional width="100%" />
          )}
          <TextArea label="Комментарий" value={contacts.comment} onChange={(v) => setContacts({ ...contacts, comment: v })} isOptional rows={2} maxLength={500} width="100%" />
          <Button label="Далее" variant="primary" size="lg" width="100%" onClick={() => validateContacts() && nav.go({ step: 'confirm' })} />
        </VStack>
      </BottomSheet>

      <BottomSheet sheetId="confirm" label="Подтверждение записи" height="hug" purpose="form">
        <VStack gap={4}>
          <SheetHeader title="Проверьте запись" onBack={back} />
          {service && nav.startsAt && (
            <MetadataList>
              <MetadataListItem label="Услуга">{service.name}</MetadataListItem>
              <MetadataListItem label="Когда">{longDateTime(nav.startsAt, tenant.timezone)}</MetadataListItem>
              <MetadataListItem label={service.duration_minutes >= 1440 ? (copy.hasCar ? 'Машина у нас до' : 'До') : 'Длительность'}>
                {service.duration_minutes >= 1440 && slotEnd ? longDateTime(slotEnd, tenant.timezone) : duration(service.duration_minutes)}
              </MetadataListItem>
              <MetadataListItem label="Стоимость">{money(service.price_cents, tenant.currency, service.price_is_from)}</MetadataListItem>
              <MetadataListItem label="Адрес">{tenant.content.address}</MetadataListItem>
              <MetadataListItem label="Контакты">
                {contacts.name}, {normalizePhone(contacts.phone)}
                {copy.hasCar && contacts.car ? `, ${contacts.car}` : ''}
              </MetadataListItem>
            </MetadataList>
          )}
          {Intl.DateTimeFormat().resolvedOptions().timeZone !== tenant.timezone && <Text type="supporting">Время указано по часовому поясу {copy.placeGen}.</Text>}
          {submitError && <Banner status="error" title={submitError} collapsible={false} />}
          <Button label={submitError ? 'Повторить' : 'Подтвердить запись'} variant="primary" size="lg" width="100%" clickAction={submit} />
          <Text type="supporting">{copy.consent}</Text>
        </VStack>
      </BottomSheet>

      <BottomSheet sheetId="done" label="Запись подтверждена" height="hug">
        <VStack gap={4}>
          <HStack gap={3} vAlign="center">
            <Icon icon={CheckCircle} size="lg" color="success" />
            <Heading level={2}>Вы записаны</Heading>
          </HStack>
          {service && nav.startsAt && (
            <Text type="large">
              {service.name} — {longDateTime(nav.startsAt, tenant.timezone)}
            </Text>
          )}
          <Text type="supporting">{tenant.content.address}</Text>
          {created && <ReminderControls bookingId={created.id} token={created.token} compact />}
          <Button label="Открыть мою запись" variant="primary" width="100%" href={`${base}my`} />
          <Button label="Готово" variant="ghost" width="100%" onClick={nav.close} />
        </VStack>
      </BottomSheet>
    </BottomSheetSwitcher>
  );
}

function SheetHeader({ title, subtitle, onBack }: { title: string; subtitle?: string; onBack: () => void }) {
  return (
    <HStack gap={2} vAlign="center">
      <Button label="Назад" isIconOnly variant="ghost" icon={<Icon icon={ArrowLeft} />} onClick={onBack} />
      <VStack gap={0.5}>
        <Heading level={2}>{title}</Heading>
        {subtitle && <Text type="supporting">{subtitle}</Text>}
      </VStack>
    </HStack>
  );
}
